/**
 * useCanvasDropTarget — registers the report canvas as a dnd-kit droppable and
 * tracks the grid cell under the cursor during a palette drag (Epic 37 canvas
 * migration). Transform-safe: reads the grid element's getBoundingClientRect()
 * (already scaled by the ZoomWrapper) and the live zoom scale, then resolves the
 * cell via the pure `pointerToGridCell` math — so the placeholder sits under the
 * cursor at ANY zoom (the RGL offset bug).
 *
 * Returns:
 *  - setNodeRef: attach to the grid element (also stored locally for rect reads).
 *  - hoverCell: the cell under the cursor (drives the live placeholder) — state,
 *    so the placeholder re-renders as you move.
 *  - getHoverCell: a ref-backed getter the DnD provider reads on drop WITHOUT
 *    depending on hover re-renders.
 *  - isOver: whether a palette drag is currently over the canvas.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { getGridDimensions } from "@provenleap/types/report-rendering";
import { pointerToGridCell, type GridCell, type GridMetrics } from "../../lib/reportDnd";
import { CANVAS_DROPPABLE_ID } from "./ReportDndProvider";

export interface CanvasDropTarget {
  setNodeRef: (el: HTMLElement | null) => void;
  hoverCell: GridCell | null;
  getHoverCell: () => GridCell | null;
  isOver: boolean;
}

export function useCanvasDropTarget(params: {
  enabled: boolean;
  scale: number;
  format: "document" | "slide-deck";
  /** The dragged item's span, to clamp the column. Null when nothing is dragging. */
  activeSpan: { colSpan: number; rowSpan: number } | null;
  paddingPx: number;
}): CanvasDropTarget {
  const { enabled, scale, format, activeSpan, paddingPx } = params;
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: CANVAS_DROPPABLE_ID,
    disabled: !enabled,
  });

  // Local element ref (for getBoundingClientRect) composed with the droppable ref.
  const elRef = useRef<HTMLElement | null>(null);
  const setNodeRef = useCallback(
    (el: HTMLElement | null) => {
      elRef.current = el;
      setDroppableRef(el);
    },
    [setDroppableRef],
  );

  const [hoverCell, setHoverCell] = useState<GridCell | null>(null);
  const hoverCellRef = useRef<GridCell | null>(null);
  hoverCellRef.current = hoverCell;
  const getHoverCell = useCallback(() => hoverCellRef.current, []);

  // Track the pointer only while a palette drag is active over the canvas. We
  // listen on the window (dnd-kit drags don't fire HTML5 dragover) and translate
  // the pointer to a grid cell. Cleared when the drag ends / leaves.
  const span = activeSpan;
  useEffect(() => {
    if (!enabled || !span) {
      setHoverCell(null);
      return;
    }
    const dims = getGridDimensions(format);
    const metrics: GridMetrics = {
      contentWidthPx: dims.contentWidthPx,
      rowHeightPx: dims.rowHeightPx,
      gapPx: dims.gapPx,
      paddingPx,
    };
    const onMove = (e: PointerEvent) => {
      const el = elRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Only show a cell when the pointer is within the grid element's box.
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        setHoverCell(null);
        return;
      }
      const cell = pointerToGridCell({ x: e.clientX, y: e.clientY }, rect, scale, metrics, span);
      setHoverCell((prev) => (prev && prev.row === cell.row && prev.col === cell.col ? prev : cell));
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [enabled, span, scale, format, paddingPx]);

  return { setNodeRef, hoverCell, getHoverCell, isOver };
}
